/**
 * WHERE SHE CAME FROM, corrected after the fact from a card or the panel, and the key that makes
 * the block a new one whenever the stored answer changes underneath it.
 */
import { useState } from 'react';
import { CHANNELS, TYPED_CHANNELS } from '@fk/shared';
import { ApiError, patchEnquiry, type EnquiryRow } from '../../api.ts';
import { Button } from '../../components/Field.tsx';
import { ErrorNotice } from '../../components/Notice.tsx';
import { SourceFields } from '../../components/SourceFields.tsx';
import { sourceAnswerOf, sourcePatch, type SourceAnswer } from '../../source.ts';

/**
 * WHAT THE CORRECTION BLOCK IS SHOWING, as a key.
 *
 * That block seeds its boxes from the row once and keeps what is typed in them, which is right
 * until the row's source changes underneath it, and on this screen it can: the question inside
 * "Work it" writes the same three columns, on the same row, from a form eight lines above. The
 * block would then be holding `phone` while the row said `nextdoor`, and its own save would send
 * `phone` back and undo the answer somebody had just been given.
 *
 * A key made of what is stored means the block is a new block whenever the stored answer changes,
 * which is exactly when its boxes stopped describing anything.
 */
export function sourceKey(row: EnquiryRow): string {
  return [row.sourceChannel, row.sourceDetail ?? '', row.referredByClientId ?? ''].join('|');
}

/**
 * THE CORRECTION, at any time and from either width.
 *
 * A source that cannot be corrected is a source that is wrong for ever: every call opens an
 * enquiry filed `phone`, and a mis-tap on the way in travels through the conversion and into the
 * household, where priority 7 counts it. The change is audited like every other, so the record
 * keeps what the channel said before.
 *
 * WHICH WORDS ARE OFFERED IS THE ROW'S OWN BUSINESS. A row carrying the id the website minted may
 * wear `website_form` and may be given it back, because that id is the proof; a row without one
 * may never be typed into it, and the server refuses it in a sentence if this screen is wrong.
 */
export function WhereSheCameFrom({
  idPrefix,
  row,
  onChanged,
}: {
  idPrefix: string;
  row: EnquiryRow;
  onChanged: (sentence: string) => void;
}) {
  const current = sourceAnswerOf(row);
  const [answer, setAnswer] = useState<SourceAnswer>(current);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /** On a refusal the server's sentence is shown and nothing typed is touched. */
  async function run(work: () => Promise<unknown>, sentence: string) {
    setBusy(true);
    setProblem(null);
    try {
      await work();
      onChanged(sentence);
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'That was not recorded.');
    } finally {
      setBusy(false);
    }
  }

  function save() {
    const patch = sourcePatch(answer, current);
    if (Object.keys(patch).length === 0) {
      setProblem('Nothing here has changed yet.');
      return;
    }
    void run(
      () => patchEnquiry(row.id, patch),
      'Saved. The history keeps what it said before this.',
    );
  }

  return (
    <div className="mt-3">
      {problem ? (
        <div className="mb-3">
          <ErrorNotice title="Not recorded" message={problem} />
        </div>
      ) : null}

      <SourceFields
        idPrefix={`${idPrefix}-source-${row.id}`}
        channels={row.leadId ? CHANNELS : TYPED_CHANNELS}
        value={answer}
        onChange={setAnswer}
        channelLabel="How they came in"
        channelHint="Corrected, never guessed. What it said before stays in the history."
        detailHint="Which Nextdoor thread, which realtor's office, which letter batch."
        websiteFormNote="This one carries the id the website minted, which is why that word is offered on it and on nothing else."
      />

      <Button onClick={save} disabled={busy} full>
        {busy ? 'Recording' : 'Save where they came from'}
      </Button>
    </div>
  );
}
